import React, { useState, useEffect, useMemo } from 'react';
import { apiCall } from '../../../../services/realApi';
import { useAuth } from '../../../../contexts/AuthContext';
import { useClient } from '../../../../contexts/ClientContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../../utils/permissions';
import { FiChevronDown as ChevronDown, FiChevronRight as ChevronRight, FiUsers as Users, FiShield as Shield, FiHome as Building, FiMail as Mail, FiClock as Clock, FiUserPlus as UserPlus, FiLock as Lock } from 'react-icons/fi';
import { Button } from '../../../../components/ui';
import InviteUserModal from './InviteUserModal';
import UserDetailsModal from './UserDetailsModal';

interface HierarchyNode {
  user_id: string;
  name: string;
  email: string;
  job_title?: string | null;
  job_title_level?: number | null;
  manager_id?: string | null;
  invited_by?: string | null;
  invited_by_name?: string | null;
  client_count?: number;
  module_count?: number;
  role_count?: number;
  last_active?: string | null;
  children?: HierarchyNode[];
}

interface HierarchyData {
  roots: HierarchyNode[];
  total_users: number;
  total_levels: number;
}

interface HierarchyInvitation {
  id: string;
  email?: string;
  status?: string;
  [key: string]: unknown;
}

interface HierarchyTabProps {
  onRefresh?: () => void;
}

const HierarchyTab: React.FC<HierarchyTabProps> = ({ onRefresh }) => {
  const { user: currentUser } = useAuth();
  const { selectedClientId } = useClient();
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [hierarchy, setHierarchy] = useState<HierarchyData | null>(null);
  const [invitations, setInvitations] = useState<HierarchyInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [inviteContext, setInviteContext] = useState<Record<string, string> | null>(null); // For inviting as a report

  // Check if user can access hierarchy (privileged internal role; no dedicated permission yet)
  const canAccessHierarchy = useMemo(() => {
    return currentUser?.role === 'aivilo_staff';
  }, [currentUser?.role]);

  // Check if user can invite AWS users (permission-based)
  const canInviteAWSUser = useMemo(() => {
    if (!currentUser) return false;
    if (isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN)) return true;
    if (hasPermission(PERMISSIONS.PARTNERS_MANAGE)) return true;
    if (isAWSUser() && hasPermission(PERMISSIONS.USERS_CREATE)) return true;
    return false;
  }, [currentUser, hasPermission, isAdmin, isAWSUser]);

  useEffect(() => {
    if (canAccessHierarchy) {
      loadHierarchy();
      loadInvitations();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessHierarchy, selectedClientId]);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build endpoint with optional client_id filter
      let endpoint = '/aws-users/hierarchy';
      if (selectedClientId) {
        endpoint += `?client_id=${selectedClientId}`;
      }
      
      const data = await apiCall(endpoint);
      setHierarchy(data);
      // Expand root nodes by default
      if (data?.roots) {
        const rootIds = data.roots.map((root: HierarchyNode) => root.user_id);
        setExpandedNodes(new Set(rootIds));
      }
    } catch (err: unknown) {
      console.error('Error loading hierarchy:', err);
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 403) {
        setError('You do not have permission to access the hierarchy. Only aivilo_staff can view this.');
      } else {
        setError((err instanceof Error ? err.message : null) || 'Failed to load hierarchy');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const data = await apiCall('/aws-users/invitations');
      setInvitations(data?.invitations || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  };

  const toggleNode = (userId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    setInviteContext(null);
    loadHierarchy();
    loadInvitations();
    if (onRefresh) onRefresh();
  };

  const handleUserClick = (user: HierarchyNode) => {
    setSelectedUserId(user.user_id);
    setShowUserDetailsModal(true);
  };

  const handleInviteAsReport = (manager: HierarchyNode) => {
    setInviteContext({ manager_id: manager.user_id, manager_name: manager.name });
    setShowInviteModal(true);
  };

  const getJobTitleColor = (level: number) => {
    switch (level) {
      case 1:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 2:
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 3:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 4:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatJobTitle = (jobTitle: string | null | undefined) => {
    if (!jobTitle) return 'N/A';
    return jobTitle
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Flatten tree for manager selection dropdown
  const flattenTree = (nodes: HierarchyNode[], result: HierarchyNode[] = []): HierarchyNode[] => {
    for (const node of nodes) {
      result.push({
        user_id: node.user_id,
        name: node.name,
        email: node.email,
        job_title: node.job_title,
        job_title_level: node.job_title_level,
        manager_id: node.manager_id
      });
      if ((node.children?.length ?? 0) > 0) {
        flattenTree(node.children!, result);
      }
    }
    return result;
  };

  const getAllUsers = () => {
    if (!hierarchy?.roots) return [];
    return flattenTree(hierarchy.roots);
  };

  const renderTreeNode = (node: HierarchyNode, level = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.user_id);
    const hasChildren = (node.children?.length ?? 0) > 0;
    const indent = level * 24;

    return (
      <div key={node.user_id} className="select-none">
        <div
          className="flex items-center py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors group"
          style={{ marginLeft: `${indent}px` }}
        >
          {/* Expand/Collapse Button */}
          <div className="w-6 flex items-center justify-center">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.user_id)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* User Avatar */}
          <div
            className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium cursor-pointer hover:bg-blue-600 transition-colors"
            onClick={() => handleUserClick(node)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUserClick(node); }} role="button" tabIndex={0}
          >
            {getInitials(node.name)}
          </div>

          {/* User Info */}
          <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleUserClick(node)}
                className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate"
              >
                {node.name}
              </button>
              {node.job_title_level && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getJobTitleColor(node.job_title_level)}`}>
                  L{node.job_title_level}: {formatJobTitle(node.job_title)}
                </span>
              )}
              {node.invited_by && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300">
                  Invited by {node.invited_by_name || 'Unknown'}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-4 mt-1">
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                <Mail className="w-3 h-3 mr-1" />
                {node.email}
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center">
                  <Building className="w-3 h-3 mr-1" />
                  {node.client_count || 0} clients
                </div>
                <div className="flex items-center">
                  <Shield className="w-3 h-3 mr-1" />
                  {node.module_count || 0} modules
                </div>
                <div className="flex items-center">
                  <Users className="w-3 h-3 mr-1" />
                  {node.role_count || 0} roles
                </div>
                <div className="flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDate(node.last_active)}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {canInviteAWSUser && (
            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleInviteAsReport(node)}
                className="text-xs"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                Invite Report
              </Button>
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="relative">
            {node.children?.map((child: HierarchyNode) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Show access denied message for non-staff users
  if (!canAccessHierarchy) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-8 text-center">
        <Lock className="w-12 h-12 mx-auto mb-4 text-yellow-600 dark:text-yellow-400" />
        <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
          Access Restricted
        </h3>
        <p className="text-yellow-800 dark:text-yellow-400">
          The AWS User Hierarchy is only accessible to aivilo_staff members.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-300">{error}</p>
        <Button
          onClick={loadHierarchy}
          variant="secondary"
          size="sm"
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Invite Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            AWS User Hierarchy
          </h2>
          {hierarchy && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {hierarchy.total_users} users across {hierarchy.total_levels} levels
            </p>
          )}
        </div>
        {canInviteAWSUser && (
          <Button
            onClick={() => {
              setInviteContext(null);
              setShowInviteModal(true);
            }}
            variant="primary"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        )}
      </div>

      {/* Hierarchy Tree */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition-colors">
        {!hierarchy?.roots || hierarchy.roots.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No hierarchy data available</p>
            <p className="text-sm">Start by inviting AWS users to build the organizational hierarchy.</p>
          </div>
        ) : (
          <div className="p-4">
            {hierarchy.roots.map((root: HierarchyNode) => renderTreeNode(root, 0))}
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteContext(null);
        }}
        onSuccess={handleInviteSuccess}
        allUsers={getAllUsers()}
        inviteContext={inviteContext}
        currentUserId={currentUser?.id || currentUser?.user_id}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={showUserDetailsModal}
        onClose={() => {
          setShowUserDetailsModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        invitations={invitations as unknown as Parameters<typeof UserDetailsModal>[0]['invitations']}
        allUsers={getAllUsers()}
        onInviteAsReport={handleInviteAsReport as Parameters<typeof UserDetailsModal>[0]['onInviteAsReport']}
        currentUserId={currentUser?.id || currentUser?.user_id}
        canInviteAWSUser={canInviteAWSUser}
      />
    </div>
  );
};

export default HierarchyTab;

